import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isAfter } from 'date-fns';
import { Button } from '@c2fo/liquidity';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useLocaleFormat from '@/utils/useLocaleFormat';
import AwardArchiveDownload from './AwardArchiveDownload';
import { getRangeKey, getRangeLabel } from './utils';

export interface RangeHeaderProps {
  startDate: string;
  endDate: string;
  latestPayDate?: string;
  latestDepositDate?: string;
  currencies?: string[];
  isDownloadDisabled: boolean;
  /**
   * The link to download the award file.
   * If this is provided, it will show a "download CSV" link in the left sidebar.
   */
  awardFileDownloadLink?: string;
  /**
   * The customers to display. If provided, the customer filter will be displayed.
   * If not provided, the customer filter will not be displayed.
   * If SEA supplir market clears flag is enabled, we need to pass around makerOrganizationUuids instead of makerIds.
   * TODO: remove number type from value array once we move to SEA
   */
  customers?: { label: string; value: (string | number)[] }[];
  /**
   * The selected customers.
   */
  selectedCustomers?: (string | number)[];
  /**
   *  Indicates whether future pay dates or deposit dates are included in history.
   */
  includeFuturePayDates: boolean;
  /**
   * Lookup map to get maker division IDs for CSV download when SEA flag is enabled.
   * Maps customer label to maker division IDs.
   */
  customerMakerIdsMap?: Map<string, Set<number>>;
  /**
   * The range key of the user's selected date range filter.
   */
  rangeKey?: string;
}

const RangeHeader = ({
  startDate,
  endDate,
  latestDepositDate,
  awardFileDownloadLink,
  customers = [],
  selectedCustomers,
  isDownloadDisabled,
  includeFuturePayDates,
  customerMakerIdsMap,
  rangeKey: rangeKeyProp,
}: RangeHeaderProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  // use rangeKey if available, otherwise use dates
  const rangeKey = useMemo(() => rangeKeyProp ?? getRangeKey(startDate, endDate), [rangeKeyProp, startDate, endDate]);

  const latestDate = latestDepositDate;
  const shouldUseLatestDate = includeFuturePayDates && latestDate && isAfter(latestDate, endDate);

  const includeFuturePayDatesMessage = t('taker.history.includesFutureDepositDates');
  const { asDateStringText } = useLocaleFormat();

  // Get maker IDs for CSV download based on feature flag and selection
  const getMakerIdsForCsv = (): string[] => {
    if (customerMakerIdsMap) {
      // SEA enabled: get division IDs from lookup map
      if (selectedCustomers?.length) {
        // Get division IDs for selected customers only
        return customers
          .filter((customer) => customer.value.some((value) => selectedCustomers.includes(value)))
          .flatMap((customer) => Array.from(customerMakerIdsMap.get(customer.label) ?? []))
          .map((id) => id.toString());
      }

      // Get division IDs for all customers
      return customers
        .flatMap((customer) => Array.from(customerMakerIdsMap.get(customer.label) ?? []))
        .map((id) => id.toString());
    }

    return customers.flatMap((c) => c.value).map((v) => v.toString());
  };

  return (
    <div className="py-8">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-2xl font-bold">{t(...getRangeLabel(rangeKey))}</div>
          <div className="text-xl">
            {t('core.dateRange', {
              formattedStartDate: asDateStringText({ date: startDate }),
              formattedEndDate: asDateStringText({ date: endDate }),
            })}
            {includeFuturePayDates && '*'}
          </div>
          {includeFuturePayDates && (
            <div className="mt-1.5 text-xs text-text-secondary">*{includeFuturePayDatesMessage}</div>
          )}
        </div>
        <div>
          {/* market history download csv link */}
          {awardFileDownloadLink && (
            <Button
              disabled={isDownloadDisabled}
              onClick={() => {
                window.location.href = awardFileDownloadLink;
                trackEnterpriseEvent('history-award-download::clicked', {
                  downloadType: 'market',
                  dateRangeType: 'depositDate',
                });
              }}
              variant="primary"
            >
              {t('core.downloadCSV')}
            </Button>
          )}
          {/* consolidated history download csv link */}
          {!!customers?.length && (
            <QueryBoundaries>
              <AwardArchiveDownload
                startDate={startDate}
                endDate={shouldUseLatestDate ? latestDate : endDate}
                makerIds={getMakerIdsForCsv()}
                disabled={isDownloadDisabled}
              />
            </QueryBoundaries>
          )}
        </div>
      </div>
    </div>
  );
};

export default RangeHeader;
